import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import CompanyProfile from './pages/CompanyProfile';
import PublicCompanyProfile from './pages/PublicCompanyProfile';
import ProfileEditor from './pages/ProfileEditor';
import RFQList from './pages/RFQList';
import RFQDetail from './pages/RFQDetail';
import CreateRFQ from './pages/CreateRFQ';
import QuotesList from './pages/QuotesList';
import CreateQuote from './pages/CreateQuote';
import FreightInsights from './pages/FreightInsights';
import Messages from './pages/Messages';
import NewsList from './pages/NewsList';
import NewsDetail from './pages/NewsDetail';
import EventsList from './pages/EventsList';
import Directory from './pages/Directory';
import SoftwareMarketplace from './pages/SoftwareMarketplace';
import Billing from './pages/Billing';
import Advertising from './pages/Advertising';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminApplications from './pages/admin/AdminApplications';
import AdminCMS from './pages/admin/AdminCMS';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import SoftwareSubmit from './pages/SoftwareSubmit';
import Charters from './pages/Charters';
import CreateCharterRFQ from './pages/CreateCharterRFQ';
import CharterRFQDetail from './pages/CharterRFQDetail';
import CreateCharterQuote from './pages/CreateCharterQuote';
import FleetManagement from './pages/FleetManagement';

// Components
import NavigationPopup from './components/NavigationPopup';
import AdExpiryNotification from './components/AdExpiryNotification';
import HelpDesk from './components/HelpDesk';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <>
      <NavigationPopup />
      <AdExpiryNotification />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/news" element={<NewsList />} />
        <Route path="/news/:newsId" element={<NewsDetail />} />
        <Route path="/events" element={<EventsList />} />
        <Route path="/directory" element={<Directory />} />
        <Route path="/company/:companyId" element={<PublicCompanyProfile />} />
        <Route path="/software" element={<SoftwareMarketplace />} />
        <Route path="/charters" element={<Charters />} />
        <Route path="/charters/:id" element={<CharterRFQDetail />} />

        {/* Protected Member Routes */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <CompanyProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <ProfileEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rfqs"
        element={
          <ProtectedRoute>
            <RFQList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rfqs/create"
        element={
          <ProtectedRoute>
            <CreateRFQ />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rfqs/:id"
        element={
          <ProtectedRoute>
            <RFQDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            <QuotesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes/create"
        element={
          <ProtectedRoute>
            <CreateQuote />
          </ProtectedRoute>
        }
      />
      <Route
        path="/charters/create"
        element={
          <ProtectedRoute>
            <CreateCharterRFQ />
          </ProtectedRoute>
        }
      />
      <Route
        path="/charters/:id/quote"
        element={
          <ProtectedRoute>
            <CreateCharterQuote />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet"
        element={
          <ProtectedRoute>
            <FleetManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <FreightInsights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/advertising"
        element={
          <ProtectedRoute>
            <Advertising />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/applications"
        element={
          <ProtectedRoute adminOnly>
            <AdminApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cms"
        element={
          <ProtectedRoute adminOnly>
            <AdminCMS />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute adminOnly>
            <AdminCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUserManagement />
          </ProtectedRoute>
        }
      />
      
      {/* Software Submission */}
      <Route
        path="/software/submit"
        element={
          <ProtectedRoute>
            <SoftwareSubmit />
          </ProtectedRoute>
        }
      />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <HelpDesk />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
